import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Youtube, Twitter, Instagram, Linkedin, Globe, BadgeCheck, Sparkles } from 'lucide-react';
import { fetchResource, fetchRelated, API } from '../api/client';

const badgeLabel = {
  verified: 'Verified',
  editors_pick: "Editor's Pick",
  rising_creator: 'Rising Creator',
  trending_tiktok: 'Trending on TikTok',
  trending_youtube: 'Trending on YouTube',
  hidden_gem: 'Hidden Gem',
  popular: 'Popular',
  weird_ai: 'Weird AI',
  beginner_friendly: 'Beginner Friendly',
  new: 'New',
};

const socialIcon = (key) => {
  const s = key.toLowerCase();
  if (s.includes('you')) return Youtube;
  if (s === 'x' || s.includes('twitter')) return Twitter;
  if (s.includes('insta')) return Instagram;
  if (s.includes('linked')) return Linkedin;
  return Globe;
};

const socialLabel = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  x: 'X (Twitter)',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  site: 'Website',
};

export default function CreatorPage() {
  const { slug } = useParams();
  const [creator, setCreator] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setCreator(null);
    setErr(null);
    fetchResource(slug)
      .then(setCreator)
      .catch(() => setErr('Creator not found'));
    fetchRelated(slug, 8).then(setRelated).catch(() => setRelated([]));
  }, [slug]);

  if (err) {
    return (
      <div className="page-container">
        <div className="empty-state">{err}</div>
        <Link to="/" className="pill-btn">Back to home</Link>
      </div>
    );
  }
  if (!creator) return <div className="page-container"><div className="empty-state">Loading…</div></div>;

  const attrs = creator.attributes || {};
  const socials = attrs.socials || {};
  const initial = creator.name.trim().charAt(0).toUpperCase();

  return (
    <div className="page-container">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/discover/rising_creator">Creators & Influencers</Link>
        <span>/</span>
        <span>{creator.name}</span>
      </div>

      <div className="tool-page-head" data-testid="creator-page-head">
        <div className="tool-page-logo" aria-hidden>
          {creator.logo ? <img src={creator.logo} alt="" /> : <span>{initial}</span>}
        </div>
        <div className="tool-page-title">
          <h1>{creator.name}</h1>
          <p className="tool-page-desc">
            <Sparkles size={12} style={{ display: 'inline', marginRight: 4 }} />
            {attrs.specialty || creator.short_description}
          </p>
          <div className="tool-badges">
            <span className="badge chip-t">Creator</span>
            {creator.badges?.map((b) => (
              <span key={b} className={`badge ${b === 'verified' ? 'chip-t' : 'featured'}`}>
                {b === 'verified' && <BadgeCheck size={10} />} {badgeLabel[b] || b}
              </span>
            ))}
          </div>
        </div>
        {creator.website_url && (
          <a
            className="pill-btn primary lg"
            href={`${API}/go/${creator.slug}?ref=/creator/${creator.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="creator-visit-btn"
          >
            Visit channel <ExternalLink size={14} />
          </a>
        )}
      </div>

      <div className="tool-page-grid">
        <div className="tool-page-main">
          <h2>About</h2>
          <p>
            {creator.full_description || `${creator.name} — ${attrs.specialty || 'creator profile'}.`}
          </p>

          {Object.keys(socials).length > 0 && (
            <>
              <h3>Find them on</h3>
              <div className="tag-list">
                {Object.entries(socials).map(([k, v]) => {
                  const Icon = socialIcon(k);
                  return (
                    <a
                      key={k}
                      href={v}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chip"
                      style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
                      data-testid={`creator-social-${k}`}
                    >
                      <Icon size={12} /> {socialLabel[k] || k}
                    </a>
                  );
                })}
              </div>
            </>
          )}

          {related.length > 0 && (
            <>
              <h3 style={{ marginTop: 24 }}>Related creators & resources</h3>
              <ul className="supp-list">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link to={r.resource_type === 'creator' ? `/creator/${r.slug}` : `/tool/${r.slug}`} className="supp-link">
                      {r.name}
                    </Link>
                    <span className="supp-cat">{r.resource_type}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <aside className="tool-page-side">
          <div className="side-block">
            <span className="muted">Specialty</span>
            <div className="side-value">{attrs.specialty || '—'}</div>
          </div>
          <div className="side-block">
            <span className="muted">Type</span>
            <div className="side-value">Creator / Influencer</div>
          </div>
          <div className="side-block">
            <span className="muted">Profile views</span>
            <div className="side-value">{(creator.view_count || 0).toLocaleString()}</div>
          </div>
          {creator.website_url && (
            <div className="side-block">
              <span className="muted">Primary link</span>
              <a
                className="side-link"
                href={`${API}/go/${creator.slug}?ref=/creator/${creator.slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {creator.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
